import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Box, Button, Paper, Typography, Stack, Chip, Divider, 
  Dialog, DialogTitle, DialogContent, IconButton, Tooltip, List
} from '@mui/material';
import { 
  VolumeUp as SpeakerIcon, 
  DeleteForever as CancelIcon, 
  Close as CloseIcon,
  NotificationsActive as NotificationsActiveIcon,
  EventBusy, 
  EventNote 
} from '@mui/icons-material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
//import { useNavigate } from 'react-router-dom';
import api from '../api/api';
// Asegúrate de que la ruta sea correcta según tu estructura
import { useCitasPaciente } from '../components/useCitasPaciente';

const TAB_NAMES = {
  VAC: "Vacunas",
  DES: "Desparasitación",
  PIP: "Pipetas",
  CIR: "Cirugías",
  TER: "Terapias",
  BAN: "Baños",
  CON: "Consulta" 
};

// === COMPONENTE INTERNO: RENDERIZADO DE LISTA DE CITAS ===
const RenderCitaList = ({ citas = [], titulo, color, icon }) => {
  if (!citas || citas.length === 0) return null;
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" color={color} fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {icon} {titulo} ({citas.length})
      </Typography>
      <List dense>
        {citas.map((cita, i) => (
          <Box key={i} sx={{ mb: 1.5, p: 1.5, bgcolor: color === 'error.main' ? '#fff5f5' : '#f0f7ff', borderRadius: 2, border: `1px solid ${color === 'error.main' ? '#feb2b2' : '#bee3f8'}` }}>
            <Chip 
              label={TAB_NAMES[cita.tab] || cita.tab} 
              size="small" 
              color={color === 'error.main' ? "error" : "primary"} 
              sx={{ mb: 1, height: '20px', fontSize: '0.7rem' }} 
            />
            <Typography variant="body2" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
              {cita.descripcion || cita.razon_consulta || 'Sin detalle'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              <strong>Fecha:</strong> {cita.fecha_cita ? new Date(cita.fecha_cita).toLocaleDateString('es-SV') : 'N/A'} | 
              <strong> Razón:</strong> {cita.razon_consulta || 'N/A'}
            </Typography>
          </Box>
        ))}
      </List>
    </Box>
  );
};

// === COMPONENTE INTERNO: MODAL DE CITAS ===
const ModalCitasPendientes = ({ idPaciente, nombrePaciente }) => {
  const [open, setOpen] = useState(false);
  const [activarCarga, setActivarCarga] = useState(false);

  // El Hook debe recibir null si no queremos que dispare el fetch
  // Asegúrate de que tu hook useCitasPaciente maneje el caso de id null (if (!id) return)
  const { vencidas, futuras, loading } = useCitasPaciente(activarCarga ? idPaciente : null);

  const handleOpen = (e) => {
    e.stopPropagation();
    setActivarCarga(true); // Solo aquí se dispara la petición a la API
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    // Opcional: setActivarCarga(false) si quieres que se limpie al cerrar
  };

  return (
    <>
      <Tooltip title="Ver Citas Pendientes">
        <IconButton 
          size="small" 
          onClick={handleOpen}
          sx={{ 
            ml: 1, 
            color: 'rgba(30, 136, 0, 229)', // Color discreto para no distraer
            '&:hover': { color: '#90CAF9', bgcolor: 'rgba(144, 202, 249, 0.1)' } 
          }}
        >
          <NotificationsActiveIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span">Citas de <strong>{nombrePaciente}</strong></Typography>
          <IconButton onClick={handleClose}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Typography sx={{ p: 2, textAlign: 'center' }}>Cargando citas...</Typography>
          ) : (
            <>
              <RenderCitaList citas={vencidas} titulo="CITAS VENCIDAS" color="error.main" icon={<EventBusy fontSize="small" />} />
              <RenderCitaList citas={futuras} titulo="CITAS PARA HOY / PRÓXIMAS" color="primary.main" icon={<EventNote fontSize="small" />} />
              {vencidas.length === 0 && futuras.length === 0 && (
                <Typography sx={{ p: 2, color: 'text.secondary' }}>No hay citas pendientes.</Typography>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// === HELPER: DECODIFICAR TOKEN ===
const decodeToken = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error decodificando token:', e);
    return null;
  }
};

const PanelRecepcion = () => {
  const [consultorios, setConsultorios] = useState([]);
  const [cola, setCola] = useState([]);
  const [llamados, setLlamados] = useState({});
  const [wsStatus, setWsStatus] = useState('conectando...');
  const [sucursalInfo, setSucursalInfo] = useState({ id: null, nombre: 'Cargando...' });
  
  const [ahora, setAhora] = useState(new Date());
  //const navigate = useNavigate();

  const token = localStorage.getItem('token');
  const payload = useRef(decodeToken(token));

  useEffect(() => {
    const timer = setInterval(() => setAhora(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!token || !payload.current) {
      setSucursalInfo({ id: null, nombre: 'Sin autenticación' });
      setWsStatus('error: sin token');
      return;
    }
    const idSucursal = payload.current.id_sucursal;
    const nombreSucursal = payload.current.nombre_sucursal || `Sucursal ${idSucursal}`;
    setSucursalInfo({ id: idSucursal, nombre: nombreSucursal });
  }, [token]);

  const hablar = (texto) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(texto);
    const voz = window.speechSynthesis.getVoices().find(v =>
      v.lang.startsWith('es') && (v.name.includes('Female') || v.name.includes('Google'))
    ) || window.speechSynthesis.getVoices()[0];
    if (voz) {
      utterance.voice = voz;
      utterance.rate = 0.95;
      utterance.pitch = 1.05;
    }
    window.speechSynthesis.speak(utterance);
  };

  useEffect(() => {
    if (!sucursalInfo.id || !token) return;
    //const wsUrl = `ws://192.168.10.13:8000/pacientes/ws/cola/${sucursalInfo.id}?token=${encodeURIComponent(token)}`;
    const wsUrl = `wss://villademio.cloud/api/pacientes/ws/cola/${sucursalInfo.id}?token=${encodeURIComponent(token)}`;
    const socket = new WebSocket(wsUrl);
    
    socket.onmessage = (event) => {
      try {
        const mensaje = JSON.parse(event.data);
        if (mensaje.cola !== undefined && mensaje.llamados !== undefined) {
          setCola(mensaje.cola || []);
          setLlamados(mensaje.llamados || {});
        } 
        if (mensaje.tipo === 'nuevo_llamado' || mensaje.tipo === 'repetir_llamado') {
          hablar(`Turno ${mensaje.numero_turno}, ${mensaje.nombre_paciente}, pase al ${mensaje.consultorio_nombre}`);
        }
      } catch (e) { console.error('Error WS:', e); }
    };

    socket.onopen = () => setWsStatus('conectado');
    socket.onclose = () => setWsStatus('reconectando...');
    return () => socket.close();
  }, [sucursalInfo.id, token]);

  useEffect(() => {
    const cargarConsultorios = async () => {
      try {
        const res = await api.get('/pacientes/consultorios');
        const filtrados = res.data.filter(c => c.id_sucursal === payload.current?.id_sucursal);
        setConsultorios(filtrados);
      } catch (err) { console.error('Error cargando consultorios:', err); }
    };
    if (payload.current?.id_sucursal) cargarConsultorios();
  }, []);

  const handleLlamar = async (consultorioId, nombreConsultorio, pacienteIdInput = null) => {
    const pId = (pacienteIdInput !== null && pacienteIdInput !== undefined) ? String(pacienteIdInput) : null;
    try {
      const url = pId ? `/pacientes/cola/llamar/${consultorioId}?paciente_id=${pId}` : `/pacientes/cola/llamar/${consultorioId}`;
      await api.post(url);
    } catch (err) {
      console.error("Error al llamar:", err);
      hablar('No hay pacientes disponibles.');
    }
  };

  const abrirFichaPaciente = async (p) => {
    let idPaciente = p.paciente_id || p.id_paciente || p.id;
    if (!idPaciente && p.numero_turno) {
      const coincidencia = cola.find(item => item.numero_turno === p.numero_turno);
      if (coincidencia) idPaciente = coincidencia.paciente_id || coincidencia.id_paciente || coincidencia.id;
    }

    if (!idPaciente) {
      alert("No se pudo obtener el ID del paciente.");
      return;
    }

    try {
      const response = await api.get(`/pacientes/${idPaciente}`);
      if (response.data && response.data.id_cliente) {
        window.open(`/clientes/${response.data.id_cliente}`, '_blank');
      } else {
        alert("Paciente encontrado, pero sin ID de cliente asociado.");
      }
    } catch (error) {
      alert("Error al conectar con la ficha.");
    }
  };

  const handleCancelar = async (p) => {
    if (!p.id) return;
    if (window.confirm(`¿Desea quitar a ${p.nombre_paciente} de la lista?`)) {
      try { await api.post(`/pacientes/cola/cancelar?registro_id=${p.id}`); } 
      catch (err) { alert("No se pudo cancelar el turno."); }
    }
  };

  const handleCancelarLlamado = async (consultorioId) => {
    if (window.confirm("¿Liberar consultorio?")) {
      try { await api.post(`/pacientes/cola/cancelar?consultorio_id=${consultorioId}`); } 
      catch (err) { console.error(err); }
    }
  };

  const handleMarcarAtendido = async (id) => api.post(`/pacientes/cola/atender/${id}`);
  const handleFinalizar = async (id) => api.post(`/pacientes/cola/finalizar/${id}`);
  const repetirLlamado = async (id) => api.post(`/pacientes/cola/repetir/${id}`);

  const tieneServicio = (tipoServicio, busqueda) => {
    if (!tipoServicio) return false;
    let servicios = typeof tipoServicio === 'string' ? JSON.parse(tipoServicio || '[]') : tipoServicio;
    return Array.isArray(servicios) && servicios.some(s => s.toLowerCase().includes(busqueda.toLowerCase()));
  };

  const calcularEspera = (horaLlegada) => {
    if (!horaLlegada) return '0m';
    const inicio = new Date(horaLlegada);
    const diffMin = Math.floor((new Date() - inicio) / 60000);
    if (diffMin < 60) return `${diffMin}m`;
    return `${Math.floor(diffMin / 60)}h ${diffMin % 60}m`;
  };

  const colaPriorizada = useMemo(() => {
    return [...cola].sort((a, b) => {
      const aE = tieneServicio(a.tipo_servicio, 'emergencia');
      const bE = tieneServicio(b.tipo_servicio, 'emergencia');
      const aS = tieneServicio(a.tipo_servicio, 'sin cita');
      const bS = tieneServicio(b.tipo_servicio, 'sin cita');
      if (aE && !bE) return -1;
      if (!aE && bE) return 1;
      const tA = new Date(a.hora_llegada).getTime() + (aS ? 20 * 60000 : 0);
      const tB = new Date(b.hora_llegada).getTime() + (bS ? 20 * 60000 : 0);
      return tA - tB;
    });
  }, [cola]);

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" fontWeight="bold" color="#1e88e5">Panel de Recepción</Typography>
        <Typography variant="h6" color="#1565c0" gutterBottom>{sucursalInfo.nombre}</Typography>
        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}> Última actualización: {ahora.toLocaleTimeString()} </Typography>
        <Chip label={wsStatus.toUpperCase()} color={wsStatus === 'conectado' ? 'success' : 'error'} size="small" />
      </Box>

      {/* SECCIÓN DE CONSULTORIOS */}
      <Stack spacing={3} sx={{ mb: 4 }}>
        {consultorios.map((c) => {
          const actual = llamados[c.id_consultorio];
          const isLlamado = actual?.estado === 'llamado';
          const isAtendido = actual?.estado === 'atendido';
          const esE = tieneServicio(actual?.tipo_servicio, 'emergencia');
          const esF = tieneServicio(actual?.tipo_servicio, 'sin cita');

          return (
            <Paper key={c.id_consultorio} elevation={6} sx={{ p: 3, borderRadius: 3, borderLeft: actual ? (esE ? '12px solid #FFD600' : esF ? '12px solid #9E9E9E' : 'none') : 'none' }}>
              <Typography variant="h6" fontWeight="bold" color="#1976d2">{c.nombre}</Typography>
              {actual ? (
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <Chip label={isAtendido ? 'ATENDIENDO' : 'LLAMADO'} size="small" sx={{ bgcolor: isAtendido ? '#388e3c' : '#d32f2f', color: 'white' }} />
                    {esE && <Chip icon={<WarningAmberIcon />} label="EMERGENCIA" size="small" sx={{ bgcolor: '#FFD600', fontWeight: 'bold' }} />}
                    {esF && <Chip icon={<DirectionsWalkIcon />} label="FORÁNEO" size="small" sx={{ bgcolor: '#9E9E9E', color: 'white', fontWeight: 'bold' }} />}
                  </Stack>
                  
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography 
                      fontWeight="bold" variant="h5" 
                      onClick={() => abrirFichaPaciente(actual)} 
                      sx={{ cursor: 'pointer', transition: '0.2s', '&:hover': { textDecoration: 'underline', color: '#1e88e5' } }} 
                    > 
                      {actual.nombre_paciente} 
                    </Typography>
                    {/* MODAL DE CITAS INTEGRADAS */}
                    <ModalCitasPendientes 
                      idPaciente={actual.paciente_id || actual.id_paciente} 
                      nombrePaciente={actual.nombre_paciente} 
                    />
                  </Stack>

                  <Typography variant="body1">Turno: <strong>{actual.numero_turno}</strong></Typography>

                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}> 
                    <Button startIcon={<SpeakerIcon />} size="small" variant="outlined" onClick={() => repetirLlamado(c.id_consultorio)}> Repetir Voz </Button> 
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}> 
                      {(typeof actual.tipo_servicio === 'string' ? JSON.parse(actual.tipo_servicio || '[]') : actual.tipo_servicio || [] ).map((s, i) => { 
                        if (s.toLowerCase().includes('emergencia') || s.toLowerCase().includes('sin cita')) return null; 
                        return ( <Chip key={i} label={s.toUpperCase()} size="small" sx={{ height: '24px', fontSize: '0.65rem', bgcolor: 'rgba(25, 118, 210, 0.08)', color: '#1976d2', border: '1px solid #1976d2', fontWeight: 'bold' }} /> ); 
                      })} 
                    </Box> 
                  </Stack> 
                </Box>
              ) : ( <Typography color="text.secondary" sx={{ mb: 2 }}>Consultorio Libre</Typography> )}
              
              <Stack direction="row" spacing={1}>
                <Button variant="contained" fullWidth onClick={() => handleLlamar(c.id_consultorio, c.nombre)} disabled={!!actual}>Llamar</Button>
                <Button variant="contained" color="success" fullWidth onClick={() => handleMarcarAtendido(c.id_consultorio)} disabled={!isLlamado}>Atender</Button>
                <Button variant="contained" color="secondary" fullWidth onClick={() => handleFinalizar(c.id_consultorio)} disabled={!isAtendido}>Finalizar</Button>
                <Button variant="outlined" color="error" fullWidth onClick={() => handleCancelarLlamado(c.id_consultorio)} disabled={!isLlamado}>No vino</Button>
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      {/* LISTA DE ESPERA CORREGIDA */}
<Paper elevation={3} sx={{ p: 3, bgcolor: '#0D1B2A', color: 'white', borderRadius: 3 }}>
  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Pacientes en Espera:</Typography>
  <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)', mb: 2 }} />
  {colaPriorizada.length === 0 ? (
    <Typography color="gray">No hay pacientes</Typography>
  ) : (
    colaPriorizada.map((p, index) => {
      // DEFINICIÓN DE IDs CLAVE
      const uniqueKey = p.id || `temp-${index}`;
      const registroId = p.id; // UUID para LLAMAR y CANCELAR (Lo que el backend necesita para la cola)
      const fichaId = p.paciente_id || p.id_paciente; // ID numérico para Ficha y Modal Citas
      
      const pE = tieneServicio(p.tipo_servicio, 'emergencia');
      const pF = tieneServicio(p.tipo_servicio, 'sin cita');
      const espera = calcularEspera(p.hora_llegada);

      return (
        <Box key={uniqueKey} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, p: 1.5, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ textAlign: 'center', minWidth: '50px' }}>
              <Chip label={p.numero_turno} size="small" sx={{ bgcolor: pE ? '#FFD600' : pF ? '#9E9E9E' : '#1e88e5', color: pE ? 'black' : 'white', fontWeight: 'bold', mb: 0.5 }} />
              <Typography sx={{ fontSize: '0.65rem', color: '#81C784', fontWeight: 'bold' }}>⏱ {espera}</Typography>
            </Box>

            <Box>
              <Stack direction="row" alignItems="center">
                <Typography 
                  onClick={() => abrirFichaPaciente(p)} 
                  sx={{ fontWeight: 'bold', color: pE ? '#FFD600' : 'white', lineHeight: 1.2, cursor: fichaId ? 'pointer' : 'default' }}
                >
                  {p.nombre_paciente}
                </Typography>
            {/* Carga en paciente espera la modal de resumen  */}   
                <ModalCitasPendientes idPaciente={p.paciente_id || p.id} nombrePaciente={p.nombre_paciente} />    

              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#90CAF9', fontWeight: 'bold' }}>{pF ? 'FORÁNEO' : 'CON CITA'}</Typography>
                {(Array.isArray(p.tipo_servicio) ? p.tipo_servicio : JSON.parse(p.tipo_servicio || '[]')).map((s, i) => {
                  if (s.toLowerCase().includes('emergencia') || s.toLowerCase().includes('sin cita')) return null;
                  return (
                    <Chip key={i} label={s.toUpperCase()} size="small" sx={{ height: '18px', fontSize: '0.6rem', color: '#90CAF9', border: '1px solid #90CAF9', bgcolor: 'transparent' }} />
                  );
                })}
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1}>
            {consultorios.map(cons => !llamados[cons.id_consultorio] && (
              <Button 
                key={cons.id_consultorio} 
                variant="contained" 
                size="small" 
                color="warning" 
                // USAMOS registroId (UUID) para que Luna y todos pasen correctamente
                onClick={() => handleLlamar(cons.id_consultorio, cons.nombre, registroId)} 
                sx={{ fontSize: '0.65rem' }}
              >
                {cons.nombre}
              </Button>
            ))}
            {/* USAMOS registroId para cancelar correctamente el turno en DB */}
            <Button variant="contained" color="error" size="small" onClick={() => handleCancelar(p)} sx={{ minWidth: '35px', px: 0 }} > 
              <CancelIcon fontSize="small" /> 
            </Button>
          </Stack>
        </Box>
      );
    })
  )}
</Paper>
    </Box>
  );
};

export default PanelRecepcion;